// src/pages/PortalSaaS/PortalShell.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../../firebase';
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import VisitorPaywall from './modules/VisitorPaywall';
import { signOut } from 'firebase/auth';
import { setDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore'; // Garanta que isso esteja importado

// Sub-componentes
import OnboardingModal from './modals/OnboardingModal';
import WorkspaceSelectorModal from './modals/WorkspaceSelectorModal';
import SidebarSaaS from './components/SidebarSaaS';
import './PortalShell.css';
import logoPTS from './PTS_Logo_jpg.jpg';

// === NOVO COMPONENTE: UPGRADE PAYWALL (INTERNO) ===
const UpgradePaywallModal = ({ onClose }) => {
    const [precoCorporativo, setPrecoCorporativo] = useState('---');

    useEffect(() => {
        const buscarPrecos = async () => {
            try {
                const getPrecos = httpsCallable(functions, 'getPrecosAtuais');
                const response = await getPrecos();
                setPrecoCorporativo(response.data.valorMatriz);
            } catch (error) {
                console.error("Falha ao buscar tabela de preços", error);
            }
        };
        buscarPrecos();
    }, []);

    const precoFormatado = precoCorporativo !== '---' 
        ? precoCorporativo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
        : '---';

    return (
        <div className="pts-fade-in" style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', maxWidth: '600px', margin: '40px auto' }}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🔒</div>
            <h2 style={{ color: '#0f172a', marginBottom: '15px' }}>Recurso Restrito ao Perfil Corporativo</h2>
            <p style={{ color: '#64748b', fontSize: '1.1rem', marginBottom: '30px', lineHeight: '1.5' }}>
                A emissão de Laudos Periciais DNIT, gestão de obras e auditoria cruzada são recursos exclusivos do plano B2B. 
                Eleve a sua conta acadêmica para o plano Corporativo e destranque a plataforma integralmente, mantendo seu histórico de IA.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                <button className="pts-btn-outline" onClick={onClose} style={{ padding: '12px 24px' }}>
                    Continuar Estudando
                </button>
                <button className="pts-btn-launch" style={{ width: 'auto', margin: 0, padding: '12px 24px' }}>
                    🚀 MIGRAR PARA CORPORATIVO (R$ {precoFormatado})
                </button>
            </div>
        </div>
    );
};
const PortalShell = () => {
    const { currentUser } = useAuth();
    const { workspaceSlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation(); // Usado para blindagem de URL

    const [loading, setLoading] = useState(true);
    const [workspaceData, setWorkspaceData] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showSelector, setShowSelector] = useState(false);
    const [userWorkspaces, setUserWorkspaces] = useState([]);

    // ESTADOS PLG E UX
    const [isDemoMode, setIsDemoMode] = useState(false);
    const [forceUpgradeModal, setForceUpgradeModal] = useState(false); // Gatilho do Tease da Sidebar

    useEffect(() => {
        if (currentUser) {
            validarAcessoAoWorkspace();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, workspaceSlug]);

    const validarAcessoAoWorkspace = async () => {
        setLoading(true);
        try {
            const checkAcesso = httpsCallable(functions, 'checkWorkspaceAccess');
            const response = await checkAcesso({ slug: workspaceSlug || null });
            const { status, currentWorkspace, allWorkspaces } = response.data;

            if (status === "NO_WORKSPACES") {
                setWorkspaceData({
                    slug: 'setup',
                    nome_exibicao: 'Seu Laboratório Digital',
                    tipo_unidade: 'PENDENTE',
                    cnpj_completo: 'Aguardando validação...',
                    config_ia: { plano: 'DEMO', saldoInteracoesIA: 0 }
                });
                setIsDemoMode(true);
                
                // O SEGREDO DA LIBERTAÇÃO:
                // Em vez de prender todo mundo no Onboarding, nós checamos silenciosamente.
                // Se tiver convite, ele "puxa" o cara pro Onboarding. Se não tiver, ele fica no VisitorPaywall.
                const checkInviteFn = httpsCallable(functions, 'checkPendingInvites');
                checkInviteFn().then(res => {
                    if (res.data.hasInvite) setShowOnboarding(true);
                }).catch(() => {});
            } 
            else if (status === "ACCESS_DENIED") {
                if (allWorkspaces && allWorkspaces.length > 0) {
                    const firstWorkspace = allWorkspaces[0];
                    const homeRoute = firstWorkspace.config_ia?.plano === 'PLANO_ESTUDANTE' ? '/chat-ia' : '/dashboard';
                    navigate(`/${firstWorkspace.slug}${homeRoute}`, { replace: true });
                } else {
                    setShowSelector(true);
                    setUserWorkspaces(allWorkspaces || []);
                }
            } 
            else if (status === "AUTHORIZED") {
                setWorkspaceData(currentWorkspace);
                setUserWorkspaces(allWorkspaces);
                setIsDemoMode(false);
                
                if (!workspaceSlug) {
                    const homeRoute = currentWorkspace.config_ia?.plano === 'PLANO_ESTUDANTE' ? '/chat-ia' : '/dashboard';
                    navigate(`/${currentWorkspace.slug}${homeRoute}`, { replace: true });
                }
            }
        } catch (error) {
            console.error("Erro na validação Shell:", error);
        } finally {
            setLoading(false);
        }
    };

    if (!currentUser) return <PortalEntryView />;
    if (loading) return (
        <div className="pts-global-loader-overlay">
            <div className="pts-spinner"></div>
            <div className="pts-loader-brand">PTS <span>IA do Asfalto</span></div>
            <div className="pts-loader-text">Consultando credenciais seguras...</div>
        </div>
    );

    if (showSelector && userWorkspaces.length > 0) {
        return <WorkspaceSelectorModal workspaces={userWorkspaces} onSelect={(slug) => { 
            setShowSelector(false); 
            setLoading(true); 
            // Apenas navega. O useEffect (linha 69) escutará a mudança de URL e re-validará automaticamente sem causar o "pisca-pisca".
            navigate(`/${slug}/chat-ia`); 
        }} />;
    }

    if (!workspaceData) return null;

    // === FASE 7: LÓGICA CORE DE WHITELIST E BLOQUEIO ACADÊMICO ===
    const isEstudante = workspaceData.config_ia?.plano === 'PLANO_ESTUDANTE';
    const rotasPermitidasEstudante = ['/chat-ia', '/configuracoes'];
    
    // Bloqueia se for estudante e a URL NÃO contiver nenhuma das rotas permitidas.
    const isRotaProibida = isEstudante && !rotasPermitidasEstudante.some(rota => location.pathname.includes(rota));
    
    // O Muro sobe se a URL for proibida OU se a Sidebar enviar o comando pelo clique no menu opaco
    const deveExibirUpgradePaywall = isRotaProibida || forceUpgradeModal;

    return (
        <div className="pts-saas-layout">
            <SidebarSaaS 
                workspace={workspaceData} 
                allWorkspaces={userWorkspaces} 
                onOpenAdmin={() => navigate(`/${workspaceData.slug}/governanca`)}
                isLocked={isDemoMode}
                isEstudante={isEstudante}
                onRestrictedClick={() => setForceUpgradeModal(true)}
            />
            
            <main className="pts-saas-content" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                <header className="pts-saas-topbar">
                    <div className="pts-logo-area">
                        <h2>PTS <span>IA do Asfalto</span></h2>
                    </div>
                    
                    {/* NOVA ÁREA DIREITA COMPLETA */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        
                        {/* Status de IA e Plano */}
                        {!isDemoMode && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: '10px' }}>
                                
                                <span style={{ fontSize: '0.65rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontWeight: '800' }}>
                                    {['PLANO_MATRIZ', 'PRO_CORPORATIVO'].includes(workspaceData.config_ia?.plano) ? 'PREMIUM' : 'BASIC'}
                                </span>
                            </div>
                        )}

                        {/* SELETOR MULTI-CNPJ MIGRADO PARA O TOPO */}
                        <div className="pts-user-chip" style={{ display: 'flex', alignItems: 'center' }}>
                            <select 
                                style={{ 
                                    padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', 
                                    background: '#f1f5f9', color: '#475569', fontWeight: '700', fontSize: '0.85rem',
                                    outline: 'none', cursor: 'pointer'
                                }}
                                value={workspaceData?.slug || ''}
                                onChange={(e) => {
                                    if (e.target.value) navigate(`/${e.target.value}/dashboard`);
                                }}
                            >
                                {userWorkspaces && userWorkspaces.map(wksp => (
                                    <option key={wksp.cnpj_completo} value={wksp.slug}>
                                        {wksp.tipo_unidade === 'MATRIZ' ? '🏢 ' : (wksp.tipo_unidade === 'EDUCACIONAL' ? '🎓 ' : '📍 ')} {wksp.nome_exibicao}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button 
                            onClick={() => navigate(`/${workspaceData.slug}/configuracoes`)}
                            title="Configurações Locais"
                            style={{
                                background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px',
                                cursor: 'pointer', fontSize: '1rem', color: '#475569', transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.borderColor = '#0369a1'}
                            onMouseOut={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                            ⚙️
                        </button>

                        <button 
                            onClick={async () => { await signOut(auth); navigate('/'); }}
                            title="Sair do Sistema"
                            style={{
                                background: '#fee2e2', border: 'none', borderRadius: '8px', padding: '8px',
                                cursor: 'pointer', fontSize: '1rem', color: '#ef4444', transition: 'all 0.2s ease', fontWeight: 'bold'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#fecaca'}
                            onMouseOut={(e) => e.currentTarget.style.background = '#fee2e2'}
                        >
                            Sair
                        </button>
                    </div>
                </header>
                
                
                <div className="pts-saas-module-container" style={{ flexGrow: 1, height: 0, padding: 0 }}>
                    {isDemoMode ? (
                        <VisitorPaywall onStartOnboarding={() => setShowOnboarding(true)} />
                    ) : deveExibirUpgradePaywall ? (
                        <UpgradePaywallModal onClose={() => { setForceUpgradeModal(false); navigate(`/${workspaceData.slug}/chat-ia`); }} />
                    ) : (
                        <Outlet context={{ workspaceData }} /> 
                    )}
                </div>

                

                {showOnboarding && (
                    <OnboardingModal 
                        user={currentUser} 
                        onComplete={(newSlug) => {
                            setShowOnboarding(false);
                            setLoading(true);
                            validarAcessoAoWorkspace(); 
                        }} 
                    />
                )}
            </main>
        </div>
    );
};

// --- VIRTINE TÉCNICA E LOGIN FLOW ---
const PortalEntryView = () => {
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isSignUpMode, setIsSignUpMode] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const location = useLocation();
    

    // LEITOR DE URL (O TAPETE VERMELHO PARA CONVIDADOS)
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const isInvite = queryParams.get('invite');
        
        if (isInvite === 'true') {
            setIsLoginModalOpen(true);
            setIsSignUpMode(true); // Joga direto pro cadastro para ele aceitar os termos
        }
    }, [location]);

    const handleGoogleLogin = async () => {
        const provider = new GoogleAuthProvider();
        // --- ADICIONE ESTA LINHA ABAIXO ---
        // Isso força o Google a sempre perguntar "Qual conta você quer usar?"
        provider.setCustomParameters({ prompt: 'select_account' });
        // ----------------------------------
        try {
            // Se for cadastro e não aceitou, barra IMEDIATAMENTE (Sem nem abrir o Google).
            if (isSignUpMode && !acceptedTerms) {
                return alert("Para criar sua conta, você deve ler e aceitar os Termos de Uso e Política de Privacidade.");
            }

            // Abre o popup do Google
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // --- A TRAVA DE SEGURANÇA (THE BOUNCER) ---
            // Verifica se o documento dele existe no banco
            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);

            if (!isSignUpMode) {
                // MODO LOGIN: O cara diz que JÁ TEM CONTA.
                if (!userSnap.exists()) {
                    // MENTIU! Ele não tem conta no banco.
                    await signOut(auth); // Desloga ele imediatamente do Firebase Auth
                    setIsSignUpMode(true); // Muda a aba para "Criar Conta"
                    alert("Conta não encontrada! Por favor, crie seu cadastro e aceite os Termos de Uso antes de entrar.");
                    return; // Encerra a função. O PortalShell (lá em cima) não vai deixar ele entrar pois o Auth ficou nulo.
                }
            } else {
                // MODO CADASTRO: O cara marcou os termos e quer entrar.
                if (!userSnap.exists()) {
                    // Cria o currículo vitalício dele.
                    await setDoc(userRef, {
                        uid: user.uid,
                        email: user.email,
                        nome: user.displayName || "Usuário",
                        dataCriacao: serverTimestamp()
                    });
                }
            }

        } catch (e) {
            console.error("Falha no login/cadastro:", e);
            // Ignora erros de "popup closed by user"
        }
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            { "@type": "Question", "name": "Como a IA audita a Viga Benkelman (DNIT 133/2025)?", "acceptedAnswer": { "@type": "Answer", "text": "O motor processa bacias deflectométricas, calcula o Raio de Curvatura e aplica correções térmicas automáticas." } },
            { "@type": "Question", "name": "A IA processa Módulo de Resiliência (DNIT 134 e 135)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Analisamos os 18 estágios de tensão para solos e misturas, gerando modelos k1, k2 e k3 instantaneamente." } },
            { "@type": "Question", "name": "A plataforma suporta a nova norma de Sedimentação (DNIT 459/2025)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Aplicamos a Lei de Stokes com precisão absoluta e correção de densímetro 151H." } },
            { "@type": "Question", "name": "Como funciona a auditoria de Marshall (DNIT 447)?", "acceptedAnswer": { "@type": "Answer", "text": "Validamos a geometria do CP pela Errata 1/2024 e calculamos Estabilidade Corrigida e Fluência." } },
            { "@type": "Question", "name": "A IA detecta falhas em ensaios de Fadiga (DNIT 183)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Identificamos o ciclo Nf de ruptura estrutural e calculamos o módulo de rigidez inicial." } },
            { "@type": "Question", "name": "O sistema audita Reologia DSR (DNIT 423/448)?", "acceptedAnswer": { "@type": "Answer", "text": "Garantimos a classificação MSCR de tráfego e validamos a curva elastomérica para polímeros SBS." } },
            { "@type": "Question", "name": "Como a IA processa o Flow Number (DNIT 184)?", "acceptedAnswer": { "@type": "Answer", "text": "Detectamos o ponto exato de fluxo terciário, eliminando a subjetividade humana no ensaio de creep." } },
            { "@type": "Question", "name": "A plataforma suporta o ensaio LAS de Ligantes (DNIT 439)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Utilizamos a teoria VECD para modelar o dano contínuo e prever a vida útil do ligante." } },
            { "@type": "Question", "name": "O motor audita Granulometria Wash (DNIT 412/2025)?", "acceptedAnswer": { "@type": "Answer", "text": "Processamos a lavagem de agregados, calculamos erro de perda e plotamos a curva de Fuller." } },
            { "@type": "Question", "name": "A IA calcula a Resistência à Tração (DNIT 136)?", "acceptedAnswer": { "@type": "Answer", "text": "Validamos a ruptura por compressão diametral e auditamos a geometria H/D normativa." } },
            { "@type": "Question", "name": "O sistema processa o Rice Test (DNIT 427)?", "acceptedAnswer": { "@type": "Answer", "text": "Auditamos vácuo e temperatura para determinar a densidade máxima teórica (Gmm) com precisão." } },
            { "@type": "Question", "name": "Como a plataforma audita a Densidade Bulk (DNIT 428)?", "acceptedAnswer": { "@type": "Answer", "text": "Calculamos o Gmb e auditamos rigorosamente o limite de 2% de absorção hídrica." } },
            { "@type": "Question", "name": "O sistema suporta ensaios de campo como Frasco de Areia (DNIT 458/2025)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Calculamos o Grau de Compactação (GC) cruzando dados de laboratório e in situ." } },
            { "@type": "Question", "name": "A IA processa o Teor de Umidade (DNIT 456)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Sincronizamos métodos Speedy e Estufa com conversão automática de base seca e úmida." } },
            { "@type": "Question", "name": "O motor suporta Módulo de Resiliência de Estabilizados (DNIT 181)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Auditamos o comportamento elástico de materiais tratados com aditivos químicos." } },
            { "@type": "Question", "name": "A plataforma audita Estabilidade de Tintas (DNER 184)?", "acceptedAnswer": { "@type": "Answer", "text": "Realizamos a perícia qualitativa de diluição e compatibilidade para sinalização viária." } },
            { "@type": "Question", "name": "O sistema audita a Deformação Permanente de Solos (DNIT 179)?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Analisamos a fluência mecânica e a integridade estrutural de materiais terrosos." } },
            { "@type": "Question", "name": "Como a IA audita o Módulo Dinâmico (DNIT 416)?", "acceptedAnswer": { "@type": "Answer", "text": "Geramos a Curva Mestra (|E*|) via ajuste sigmoidal e shift factors térmicos em segundos." } }
        ]
    };
    return (
        <div className="pts-landing-page">
            <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            <title>PTS IA do Asfalto | Inteligência Pericial e Gestão de Ativos</title>
            
            <nav className="pts-landing-nav">
                <div className="pts-logo">
                    <img src={logoPTS} alt="Logo PTS" className="pts-logo-img" />
                    <span>IA do Asfalto</span>
                </div>
                <div className="pts-nav-links">
                    <a href="#workforce">Mão de Obra Digital</a>
                    <a href="#gestao">BI & Ativos</a>
                    <a href="#biblia">Normas</a>
                    <button className="pts-btn-nav-primary" onClick={() => setIsLoginModalOpen(true)}>
                        Acessar Plataforma
                    </button>
                </div>
            </nav>
    {/* MODAL DE AUTENTICAÇÃO (LOGIN / CADASTRO) */}
            {isLoginModalOpen && (
                <div className="pts-modal-overlay">
                    <div className="pts-login-modal">
                        <button className="pts-close-modal" onClick={() => setIsLoginModalOpen(false)}>✕</button>
                        
                        <h2>{isSignUpMode ? 'Criar Nova Conta' : 'Acesse sua Estação'}</h2>
                        <p>{isSignUpMode ? 'Leia e aceite os termos para configurar sua inteligência.' : 'A inteligência pericial aguarda seus dados.'}</p>
                        
                        <div className="pts-login-methods">
                            {/* EMAIL & SENHA (Desabilitado visualmente por enquanto, ou deixe como está) */}
                            <div className="pts-email-login">
                                <input type="email" placeholder="E-mail corporativo" className="pts-input-classic" />
                                <input type="password" placeholder="Senha" className="pts-input-classic" />
                                <button className="pts-btn-classic">
                                    {isSignUpMode ? 'Cadastrar e-mail corporativo' : 'Entrar com Senha'}
                                </button>
                            </div>

                            <div className="pts-divider"><span>OU</span></div>

                            {/* GOOGLE AUTH */}
                            <button 
                                className={`pts-btn-google-modal ${isSignUpMode && !acceptedTerms ? 'disabled' : ''}`} 
                                onClick={handleGoogleLogin}
                            >
                                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/250px-Google_%22G%22_logo.svg.png" alt="G" width="20" />
                                {isSignUpMode ? 'Cadastrar com Google' : 'Continuar com Google'}
                            </button>
                        </div>

                        {/* CAIXA DE TERMOS (Aparece APENAS no Cadastro) */}
                        {isSignUpMode && (
                            <div className="pts-modal-terms">
                                <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={acceptedTerms} 
                                        onChange={e => setAcceptedTerms(e.target.checked)} 
                                        style={{ marginTop: '4px' }}
                                    />
                                    <span style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                        Confirmo que li e aceito integralmente os <a href="/termos-e-condicoes" target="_blank" rel="noreferrer">Termos de Uso</a> e a <a href="/politica-de-privacidade" target="_blank" rel="noreferrer">Política de Privacidade</a> da PTS.
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* SWITCH DE MODOS (Muda entre Login e Cadastro) */}
                        <div className="pts-auth-switch">
                            {isSignUpMode ? (
                                <span>Já possui uma conta? <button onClick={() => setIsSignUpMode(false)}>Faça login</button></span>
                            ) : (
                                <span>Novo usuário? <button onClick={() => setIsSignUpMode(true)}>Criar conta</button></span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        

            {/* HERO: CINEMATIC IMPACT */}
            <section className="pts-landing-hero">
                <div className="pts-hero-copy">
                    <span className="pts-badge-premium">Solução para o Gestor</span>
                    <h1>A Engenharia Sênior <span>delegada</span> à IA.</h1>
                    <p>O fim das pilhas de papel e horas de análise subjetiva. Suba os dados da máquina e deixe que nossa Força de Trabalho Digital audite, calcule e delibere. Você assume o comando estratégico.</p>
                </div>
                <div className="pts-hero-visual-frame">
                </div>
            </section>

            {/* SECTION: WORKFORCE AS A SERVICE (FOCO NO DONO E RT) */}
            <section id="workforce" className="pts-section-workforce">
                <div className="pts-container">
                    <span className="pts-label-accent">IA Estrategista para o Proprietário</span>
                    <h2>Contrate seu Departamento de Engenharia <span>Digital</span>.</h2>
                    <p>Delegue o raciocínio complexo para Agentes Especialistas que aprendem o DNA da sua empresa. Mais do que um laudo, você recebe uma consultoria pericial instantânea.</p>
                    
                    <div className="pts-workforce-grid-samsung">
                        <div className="pts-w-card">
                            <div className="w-icon">🧠</div>
                            <h3>Mesa Técnica Multiagentes</h3>
                            <p>Contrate o conselho: Agentes de Materiais e Qualidade debatem sobre o traço e o custo de insumos, entregando o veredito otimizado para o seu lucro e conformidade.</p>
                        </div>
                        <div className="pts-w-card">
                            <div className="w-icon">🛡️</div>
                            <h3>Proteção do Ativo Físico</h3>
                            <p>A IA monitora a "saúde" das suas prensas. Detectamos folgas mecânicas e escorregamentos de sensores LVDT, prevenindo multas e paradas não planejadas de laboratório.</p>
                        </div>
                        <div className="pts-w-card">
                            <div className="w-icon">📑</div>
                            <h3>Laudo-Cérebro</h3>
                            <p>O engenheiro assina com paz de espírito. A IA executa a perícia forense, cálculos e análise climática, mitigando 100% do risco de erro humano.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION: GESTÃO DE ALTO NÍVEL (DASHBOARD DO DIRETOR) */}
            <section id="gestao" className="pts-section-management">
                <div className="pts-container">
                    <div className="pts-management-content">
                        <div className="pts-m-text">
                            <span className="pts-label-accent">SOLUÇÃO PARA O SOCIO ADMINISTRADOR</span>
                            <h2>Gerencie a Inteligência, <span>não a papelada</span>.</h2>
                            <p>Para o Diretor, A PTS IA DO ASFALTO transcende as unidades físicas. Identifique padrões de performance de sua empresa, preveja analisando tendências de desgastes de equipamentos caros e tenha o controle total da sua conformidade normativa em um único painel global.</p>
                            
                        </div>
                        <div className="pts-m-visual">
                            <div className="pts-bi-card">
                                <strong>Performance Multi-Filial</strong>
                                <div className="pts-bi-bar"><span>Filial Itajaí: 98% Conformidade</span></div>
                                <div className="pts-bi-bar"><span>Filial SP: 92% Conformidade</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION: WORKFORCE AS A SERVICE (MESA TÉCNICA) */}
            <section id="mesa" className="pts-section-workforce">
                <div className="pts-container">
                    <span className="pts-label-accent">EXPERT-AS-A-SERVICE</span>
                    <h2>Contrate seu Departamento de Engenharia <span>Digital</span>.</h2>
                    <p>Delegue as tarefas mais complexas para nossos Agentes Especialistas. Eles aprendem o seu histórico e deliberam como uma Mesa Técnica de elite para otimizar seus custos.</p>
                    
                    <div className="pts-workforce-grid">
                        <div className="pts-w-card">
                            <div className="w-icon">🧠</div>
                            <h3>Deliberação Autônoma</h3>
                            <p>Seus Agentes de Materiais, Qualidade e Orçamento debatem entre si para encontrar o traço que equilibra conformidade DNIT e economia de insumos.</p>
                        </div>
                        <div className="pts-w-card">
                            <div className="w-icon">📂</div>
                            <h3>Pasta de Contexto Inteligente</h3>
                            <p>A IA consome seus editais e históricos de jazidas. As respostas não são genéricas; elas são baseadas na realidade geológica da sua empresa.</p>
                        </div>
                        <div className="pts-w-card">
                            <div className="w-icon">📑</div>
                            <h3>Laudo Auditável em Segundos</h3>
                            <p>Eliminamos o gargalo do engenheiro sênior. A IA mastiga os ensaios complexos (VECD, LAS, Módulo Dinâmico) e entrega o parecer técnico mastigado.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION: A BÍBLIA DE NORMAS (PARA O RESPONSÁVEL TÉCNICO) */}
            <section id="biblia" className="pts-section-library">
                <div className="pts-container">
                    <h2 className="pts-title-white">A Bíblia de Normas Digitalizadas</h2>
                    <p className="pts-subtitle-dim">O Hub definitivo para laboratórios de elite, universidades e concessionárias.</p>
                    
                    <div className="pts-norms-mega-accordion">
                        <details open>
                            <summary>🛣️ Pavimentação & Mecânica Dinâmica</summary>
                            <div className="pts-norm-grid-samsung">
                                <div className="n-unit"><strong>DNIT 133: Viga Benkelman</strong><p>Auditoria de bacia, raio de curvatura e correção térmica imediata (2025).</p></div>
                                <div className="n-unit"><strong>DNIT 134/135: Resiliência</strong><p>Cálculo de Mr em 18 estágios com modelos k1-k3 automáticos para solos e misturas.</p></div>
                                <div className="n-unit"><strong>DNIT 416: Módulo Dinâmico</strong><p>Geração de Curva Mestra (|E*|) via ajuste sigmoidal e shift factors térmicos.</p></div>
                                <div className="n-unit"><strong>DNIT 183: Fadiga Asfáltica</strong><p>Determinação de Nf e rigidez inicial no centésimo ciclo de carga repetida.</p></div>
                                <div className="n-unit"><strong>DNIT 184: Flow Number</strong><p>Detecção algorítmica do ponto de fluxo terciário, eliminando subjetividade manual.</p></div>
                                <div className="n-unit"><strong>DNIT 434: Fadiga Estabilizados</strong><p>Análise de vida útil para materiais tratados com aditivos e cimento.</p></div>
                                <div className="n-unit"><strong>DNIT 447: Marshall (2024)</strong><p>Estabilidade e Fluência corrigidas conforme Errata 1 e volumetria Gmb integrada.</p></div>
                                <div className="n-unit"><strong>DNIT 179: Deformação Solos</strong><p>Auditoria forense de fluência mecânica para suporte de subleito.</p></div>
                                <div className="n-unit"><strong>DNIT 136: Resistência (RT)</strong><p>Validação de ruptura por compressão diametral e simetria do CP.</p></div>
                            </div>
                        </details>

                        <details>
                            <summary>🧪 Geotecnia, Caracterização & Reologia</summary>
                            <div className="pts-norm-grid-samsung">
                                <div className="n-unit"><strong>DNIT 459: Sedimentação</strong><p>Aplicação da Lei de Stokes com correção térmica de densímetro 151H (2025).</p></div>
                                <div className="n-unit"><strong>DNIT 412: Granulometria Wash</strong><p>Processamento de lavagem, erro de perda e plotagem Potência 0.45.</p></div>
                                <div className="n-unit"><strong>DNIT 423/448: Reologia DSR</strong><p>Classificação MSCR de tráfego e verificação pericial de polímeros SBS.</p></div>
                                <div className="n-unit"><strong>DNIT 439: LAS Fadiga Binder</strong><p>Modelagem complexa via VECD para previsão de dano estrutural em ligantes.</p></div>
                                <div className="n-unit"><strong>DNIT 427/428: Rice e Bulk</strong><p>Cálculo de Gmm e Gmb com auditoria rigorosa de absorção hídrica.</p></div>
                                <div className="n-unit"><strong>DNIT 456/458: Campo e Umidade</strong><p>Controle de Grau de Compactação e métodos Speedy/Estufa sincronizados.</p></div>
                                <div className="n-unit"><strong>DNER 184: Estabilidade Tintas</strong><p>Perícia química de diluição para sinalização viária horizontal.</p></div>
                                <div className="n-unit"><strong>DNIT 181: Resiliência Estabilizados</strong><p>Módulo de resiliência para solos tratados com aditivos químicos.</p></div>
                                <div className="n-unit"><strong>DNIT 134 Corrigida: Forense</strong><p>Rastreabilidade total para auditoria de ensaios triaxiais e geotecnia.</p></div>
                            </div>
                        </details>
                    </div>
                </div>
            </section>

            <footer className="pts-landing-footer">
                <div className="pts-logo">
                    {/* LOGO DA LANDING PAGE AGORA VOLTA PARA A HOME */}
                    <Link to="/">
                        <img src={logoPTS} alt="Logo PTS" className="pts-logo-img" style={{ cursor: 'pointer' }} />
                    </Link>
                    <span>PTS.DEV</span>
                    <div className="pts-hub-status">
                        <span className="active">🧪 IA do Asfalto</span>
                        <span className="lock">🏗️ IA do Concreto</span>
                        <span className="lock">🛤️ IA de Ferrovias</span>
                    </div>
                </div>
                <p className="pts-copy">© 2026 PTS IA do Asfalto. Uma solução de SAAS da Primo Trade Solutions LTDA.</p>
            </footer>
        </div>
    );
};
export default PortalShell;